/**
 * Cadência em foreground.
 *
 * 5 minutos no lugar dos 60s fixos do mecanismo anterior: nada nesta política é
 * urgente — o reload espera o app ficar ocioso de qualquer forma — e o corte de
 * volume de request é grande, multiplicado por todo participante de um evento
 * ao vivo.
 */
export const FOREGROUND_POLL_MS = 5 * 60_000

/** Gatilhos em que vale checar na hora, sem esperar a cadência. */
const IMMEDIATE_CHECK_EVENTS = ['visibilitychange', 'focus', 'online'] as const

export type VersionPayload = {
  version: string | null
  build: string | null
  releaseType: string | null
  releasedAt: string | null
  minimumVersion: string | null
}

type CheckerTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>

type VersionCheckerOptions = {
  target?: CheckerTarget
  fetcher?: typeof fetch
  isVisible?: () => boolean
  onResult: (payload: VersionPayload) => void
  onError: (error: Error) => void
  intervalMs?: number
  endpoint?: string
  /** Desliga tudo — é o que mantém o dev sem polling e sem reload. */
  enabled?: boolean
}

/**
 * Consulta a versão publicada com cadência adaptativa: 5min em foreground,
 * nada em background, e checagem imediata quando a aba volta, ganha foco ou a
 * rede reconecta.
 *
 * Toda falha vira `onError` — nunca silêncio. O `catch (error) {}` do mecanismo
 * anterior fazia o watcher morrer sem ninguém perceber, e nenhuma das duas
 * perguntas que importam ("o endpoint está saudável?", "quantos usuários estão
 * desatualizados?") tinha resposta.
 */
export const createVersionChecker = ({
  target,
  fetcher,
  isVisible,
  onResult,
  onError,
  intervalMs = FOREGROUND_POLL_MS,
  endpoint = '/api/version',
  enabled = true
}: VersionCheckerOptions) => {
  const alvo =
    target ?? (typeof window === 'undefined' ? null : (window as CheckerTarget))

  const buscar = fetcher ?? (typeof fetch === 'undefined' ? null : fetch)

  const estaVisivel =
    isVisible ??
    (() => (typeof document === 'undefined' ? true : !document.hidden))

  let timer: ReturnType<typeof setInterval> | null = null
  let ativo = false

  const checar = async () => {
    if (!ativo || !buscar) return
    if (!estaVisivel()) return

    try {
      const resposta = await buscar(endpoint, { cache: 'no-store' })

      if (!resposta.ok)
        throw new Error(`GET ${endpoint} respondeu ${resposta.status}`)

      onResult((await resposta.json()) as VersionPayload)
    } catch (erro) {
      onError(erro instanceof Error ? erro : new Error(String(erro)))
    }
  }

  const aoGatilhoImediato = () => {
    void checar()
  }

  return {
    start: () => {
      if (ativo || !enabled) return

      ativo = true
      IMMEDIATE_CHECK_EVENTS.forEach(evento =>
        alvo?.addEventListener(evento, aoGatilhoImediato)
      )
      // O intervalo roda sempre; quem decide não bater é `checar`, olhando a
      // visibilidade no momento — mais simples que parar e recriar timer a cada
      // troca de aba, e com o mesmo efeito no volume de request.
      timer = setInterval(() => void checar(), intervalMs)
      void checar()
    },

    dispose: () => {
      if (!ativo) return

      ativo = false
      if (timer !== null) clearInterval(timer)
      timer = null
      IMMEDIATE_CHECK_EVENTS.forEach(evento =>
        alvo?.removeEventListener(evento, aoGatilhoImediato)
      )
    },

    checkNow: () => checar()
  }
}

export type VersionChecker = ReturnType<typeof createVersionChecker>
